import { useEffect, useState } from 'react';
import { UserService } from '../services/user.service';
import { displayUsername } from '../utils/userUtils';
import { shortRef } from '../utils/expenseDisplay';

/** Anything carrying an employee user id and (optionally) the backend-resolved name. */
export interface EmployeeRef {
    employeeUserId: string;
    employeeName?: string | null;
}

/**
 * Employee display-name resolver for expense claims/advances. The backend now returns
 * {@code employeeName} on every claim/advance DTO (readable by anyone who can view the row, e.g. an
 * accountant without the user-list permission), so that is used first. Only rows WITHOUT a name fall
 * back to the institution user list (first 200 users, loaded once and only when {@code rows} contains
 * such a row) and finally to a short readable reference. When the DTO provides every name the
 * (possibly 403ing) user-list endpoint is never called.
 */
export const useEmployeeNames = (rows: EmployeeRef[] = []): ((row: EmployeeRef | string) => string) => {
    const [names, setNames] = useState<Record<string, string>>({});
    const needsLookup = rows.some(r => !r.employeeName);

    useEffect(() => {
        if (!needsLookup) return;
        let cancelled = false;
        UserService.getInstitutionUsers(0, 200)
            .then((res: any) => {
                if (cancelled) return;
                const map: Record<string, string> = {};
                (res?.content ?? []).forEach((u: any) => {
                    map[u.id] = u.fullName || displayUsername(u.username) || '';
                });
                setNames(map);
            })
            .catch(() => { /* fall back to short references */ });
        return () => { cancelled = true; };
    }, [needsLookup]);

    return (row: EmployeeRef | string) => {
        const id = typeof row === 'string' ? row : row.employeeUserId;
        const own = typeof row === 'string' ? null : row.employeeName;
        return own || names[id] || shortRef('EMP', id);
    };
};
